import { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { useQuery } from '@tanstack/react-query'
import { t } from 'i18next'

import EmptyPage from '~/components/empty-page'
import Loading from '~/components/Loading'
import LoadingPopUp from '~/components/LoadingPopUp'
import { isAdvertiser, isPublisher } from '~/helpers'
import { useAlert } from '~/hooks/useAlert'
import { useAppInfo } from '~/hooks/useAppInfo'
import { useAppSelector } from '~/store/hooks'

import { listCheckingAccountService } from '../../services/checking-account'
import actions from '../../store/checkingAccount/actions'
import { buildCheckingAccountListParams } from './buildListParams'
import { CheckingAccountTableContent } from './CheckingAccountTableContent'
import { CheckingAccountTableToolbar } from './CheckingAccountTableToolbar'
import { getCheckingAccountErrorMessage } from './getCheckingAccountErrorMessage'

import styles from './styles.module.css'

const CheckingAccountTable = () => {
  const { isWhiteLabel, hasRecharge } = useAppInfo()
  const dispatch = useDispatch()
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const { accountType } = useAppSelector(state => state.signin)
  const accountKey = accountType === 'grocery' ? 'advertiser' : 'publisher'

  const showRechargeColumn = isAdvertiser() && hasRecharge

  const {
    checkingAccount: {
      list: {
        page,
        perPage,
        sortDirection,
        sortKey,
        sortDirectionDefault,
        sortKeyDefault
      }
    }
  } = useAppSelector(state => state.retailMedia)

  const {
    retailMedia: { queryFilterWallet }
  } = useAppSelector(state => state.filters)

  const activeSortKey = sortKey || sortKeyDefault
  const activeSortDirection = sortDirection || sortDirectionDefault

  const params = useMemo(
    () =>
      buildCheckingAccountListParams({
        page,
        perPage,
        queryFilterWallet,
        sortDirection,
        sortDirectionDefault,
        sortKey,
        sortKeyDefault
      }),
    [
      page,
      perPage,
      queryFilterWallet,
      sortDirection,
      sortDirectionDefault,
      sortKey,
      sortKeyDefault
    ]
  )

  const {
    data: checkingAccountList,
    error,
    isFetching
  } = useQuery({
    queryKey: [
      'checking-account',
      page,
      perPage,
      queryFilterWallet,
      sortKey,
      sortDirection
    ],
    queryFn: async () => {
      const response = await listCheckingAccountService(params)
      return response.data
    }
  })

  const [AlertOnError] = useAlert(getCheckingAccountErrorMessage(error))

  const handlePagination = useCallback(
    (nextPage: number) => {
      dispatch(actions.setPage(nextPage))
    },
    [dispatch]
  )

  const handleItemsPerPageChange = useCallback(
    ({ value }: { value: number }) => {
      dispatch(actions.setPerPage(value))
    },
    [dispatch]
  )

  const handleSort = useCallback(
    (data: OnSortProps) => {
      dispatch(actions.setSorting(data))
    },
    [dispatch]
  )

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const showFilters = isPublisher() || (!isWhiteLabel && isAdvertiser())
  const rows = checkingAccountList?.data ?? []
  const total = checkingAccountList?.total ?? 0

  return (
    <div className={styles.page}>
      <CheckingAccountTableToolbar showFilters={showFilters} total={total} />

      <Loading status={!checkingAccountList && isFetching}>
        {t('common:actions.loadingData')}...
      </Loading>

      <LoadingPopUp isActive={isFetching} />

      <AlertOnError />

      {checkingAccountList?.total === 0 && (
        <EmptyPage
          title={t('common:table.empty')}
          subtitle={t('common:table.emptyFiltered')}
          titleButton={t('common:words.clearFilters')}
          svgPath="photos/orders-empty"
        />
      )}

      {total > 0 ? (
        <CheckingAccountTableContent
          rows={rows}
          total={total}
          pages={checkingAccountList?.pages ?? 0}
          page={page}
          perPage={perPage}
          accountKey={accountKey}
          showRechargeColumn={showRechargeColumn}
          expandedIds={expandedIds}
          activeSortKey={activeSortKey}
          activeSortDirection={activeSortDirection}
          onSort={handleSort}
          onToggleExpanded={toggleExpanded}
          onPagination={handlePagination}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      ) : null}
    </div>
  )
}

export default CheckingAccountTable
